<template>
  <div class="vx-row">
    <!-- ROW 1: MOBILE APP LOGIN QR CODE & PRINT CARD -->
    <div class="vx-col w-full mb-base">
      <vx-card :title="'Mobile App Login QR Code'" no-shadow>
        <div class="flex flex-wrap items-center justify-between mb-4">
          <p class="text-sm text-muted mb-2">
            Scan this QR code with the mobile application to quickly sign in, or print it as a card.
          </p>
          <vs-button
            icon-pack="feather"
            icon="icon-printer"
            color="primary"
            class="mb-2"
            @click="onClickPrintCard"
          >
            Print Card
          </vs-button>
        </div>

        <div class="flex flex-wrap items-start">
          <!-- Printable Business Card Preview -->
          <div id="mobile-app-business-card" class="business-card-preview mr-8 mb-4">
            <div class="card-top-stripe"></div>
            <div class="card-header">
              <img :src="companyLogo" alt="Company Logo" class="card-logo" />
              <span class="card-corp-badge">A BRAVER FLAVOUR</span>
            </div>

            <div class="card-body">
              <div class="card-user-info">
                <div class="card-user-profile-wrap">
                  <img
                    v-if="userAvatar"
                    :src="userAvatar"
                    alt="User Photo"
                    class="card-user-avatar"
                  />
                  <div v-else class="card-user-avatar-placeholder">
                    {{ userInitials }}
                  </div>
                  <div class="card-user-text">
                    <div class="card-user-name">{{ userName }}</div>
                    <div v-if="userRole" class="card-user-role">{{ userRole }}</div>
                  </div>
                </div>
                <div class="card-user-email">
                  <svg class="email-icon" viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                  <span>{{ userEmail || 'N/A' }}</span>
                </div>
              </div>

              <!-- QR & Mobile Access Column -->
              <div class="card-qr-column">
                <div class="card-qr-box">
                  <qrcode-vue
                    :value="qrData"
                    :size="80"
                    :margin="0"
                    render-as="svg"
                    style="display:block; background:#ffffff;"
                    level="M"
                  />
                </div>
                <span class="card-qr-label">MOBILE APP ACCESS</span>
              </div>
            </div>

            <div class="card-footer">
              <span class="card-footer-text">INTERNATIONAL TASTE SOLUTIONS</span>
              <div class="brand-dots">
                <span class="brand-dot-pink"></span>
                <span class="brand-dot-yellow"></span>
                <span class="brand-dot-teal"></span>
              </div>
            </div>
          </div>

          <!-- Instructions & Tips -->
          <div class="flex-grow mb-4" style="max-width: 460px;">
            <div class="mb-3">
              <small class="text-muted block font-semibold mb-1">Account Email</small>
              <span class="font-bold text-lg text-primary">{{ userEmail || 'N/A' }}</span>
            </div>

            <div class="flex items-start mb-2">
              <feather-icon icon="CheckCircleIcon" svgClasses="w-4 h-4 text-success mr-2 mt-1 flex-shrink-0" />
              <span class="text-sm">Click <strong>Print Card</strong> to print your card.</span>
            </div>

            <div class="flex items-start mb-2">
              <feather-icon icon="CheckCircleIcon" svgClasses="w-4 h-4 text-success mr-2 mt-1 flex-shrink-0" />
              <span class="text-sm">Open the mobile application and tap <strong>Scan QR</strong> to log in.</span>
            </div>

            <div class="flex items-start">
              <feather-icon icon="CheckCircleIcon" svgClasses="w-4 h-4 text-success mr-2 mt-1 flex-shrink-0" />
              <span class="text-sm">Enter your 6-digit Mobile App PIN when prompted to complete verification.</span>
            </div>
          </div>
        </div>
      </vx-card>
    </div>
    <!-- ROW 2 COL 1: VIEW MOBILE APP PIN -->
    <div class="vx-col md:w-1/2 w-full mb-base">
      <vx-card :title="'View Mobile App PIN'" no-shadow class="h-full">
        <p class="text-sm text-muted mb-4">
          Enter your account password to view your current 6-digit Mobile App PIN code.
        </p>

        <!-- Polite notice when PIN is not set yet -->
        <div v-if="no_pin_message" class="p-4 rounded border border-solid d-theme-border-grey-light bg-light-primary mb-4">
          <div class="flex items-start">
            <feather-icon icon="InfoIcon" svgClasses="w-5 h-5 text-primary mr-3 flex-shrink-0 mt-1" />
            <div class="flex-grow">
              <div class="font-semibold text-primary mb-1">Notice</div>
              <p class="text-sm mb-1 text-dark">{{ no_pin_message }}</p>
              <small class="text-muted block">You can set your 6-digit PIN anytime using the <strong>Update Mobile App PIN</strong> form on the right.</small>
            </div>
            <feather-icon icon="XIcon" svgClasses="w-4 h-4 cursor-pointer text-muted hover:text-dark ml-2" @click="no_pin_message = ''" />
          </div>
        </div>

        <!-- If PIN is NOT revealed yet -->
        <div v-if="!is_pin_viewed" id="view-pin-container">
          <ValidationObserver ref="viewPinForm" v-slot="{ handleSubmit }">
            <form @submit.prevent="handleSubmit(onClickViewPin)" autocomplete="off">
              <div class="vx-row mb-3">
                <div class="vx-col w-full">
                  <div class="relative">
                    <ValidationProvider vid="view_password" name="Password" rules="required" v-slot="{ errors }">
                      <small class="label">Account Password</small>
                      <vs-input
                        :type="showViewPassword ? 'text' : 'password'"
                        name="view_password"
                        class="w-full"
                        placeholder="Enter your current password"
                        v-model="view_password"
                      />
                      <span class="text-danger text-sm">{{ errors[0] }}</span>
                      <div
                        @click.stop="showViewPassword = !showViewPassword"
                        class="flex items-center text-gray-500 focus:outline-none show-button"
                        :class="{ 'mt-3': !errors[0] }"
                      >
                        <span v-if="showViewPassword">
                          <img class="float-right" src="@/assets/images/pages/login/hidden.png" width="16px" height="16px" />
                        </span>
                        <span v-else>
                          <img class="float-right" src="@/assets/images/pages/login/show.png" width="16px" height="16px" />
                        </span>
                      </div>
                    </ValidationProvider>
                  </div>
                </div>
              </div>

              <div class="flex items-center">
                <vs-button button="submit" icon-pack="feather" icon="icon-eye" class="mt-2">
                  View PIN
                </vs-button>
              </div>
            </form>
          </ValidationObserver>
        </div>

        <!-- If PIN is revealed -->
        <div v-else class="p-4 rounded border border-solid d-theme-border-grey-light bg-light-primary">
          <div class="flex flex-wrap items-center justify-between">
            <div class="mb-2">
              <small class="text-muted block font-semibold mb-1">Your Mobile App PIN</small>
              <div class="text-3xl font-bold tracking-widest text-primary font-mono">
                {{ is_pin_masked ? '••••••' : pin_data.mobile_app_pin }}
              </div>
              <small v-if="pin_data.mobile_app_pin_updated_at" class="text-muted block mt-2">
                Last updated: {{ formatDateTime(pin_data.mobile_app_pin_updated_at) }}
              </small>
            </div>

            <div class="flex items-center flex-wrap mt-2">
              <vs-button
                type="border"
                size="small"
                class="mr-2 mb-2"
                icon-pack="feather"
                :icon="is_pin_masked ? 'icon-eye' : 'icon-eye-off'"
                @click="is_pin_masked = !is_pin_masked"
              >
                {{ is_pin_masked ? 'Reveal PIN' : 'Mask PIN' }}
              </vs-button>

              <vs-button
                type="border"
                size="small"
                class="mr-2 mb-2"
                icon-pack="feather"
                icon="icon-copy"
                @click="copyPinToClipboard"
              >
                Copy PIN
              </vs-button>

              <vs-button
                type="border"
                size="small"
                color="warning"
                class="mb-2"
                icon-pack="feather"
                icon="icon-lock"
                @click="onClickLockPin"
              >
                Lock & Hide
              </vs-button>
            </div>
          </div>
        </div>
      </vx-card>
    </div>

    <!-- ROW 2 COL 2: UPDATE 6-DIGIT MOBILE APP PIN -->
    <div class="vx-col md:w-1/2 w-full mb-base">
      <vx-card :title="'Update Mobile App PIN'" no-shadow class="h-full">
        <p class="text-sm text-muted mb-4">
          Set or update your 6-digit numeric PIN for mobile app login.
        </p>

        <div id="update-pin-container" class="mobile-app-pin-card">
          <form @submit.prevent="onClickUpdatePin" autocomplete="off">
            <!-- New PIN (6 Boxes) -->
            <div class="mb-4">
              <div class="flex items-center mb-2">
                <small class="label font-semibold">New 6-Digit PIN</small>
                <div
                  v-if="mobile_app_pin && mobile_app_pin.length > 0"
                  @click.stop="showNewPin = !showNewPin"
                  class="cursor-pointer text-xs flex items-center text-primary ml-3"
                >
                  <feather-icon :icon="showNewPin ? 'EyeOffIcon' : 'EyeIcon'" svgClasses="w-4 h-4 mr-1" />
                  <span>{{ showNewPin ? 'Mask PIN' : 'Reveal PIN' }}</span>
                </div>
              </div>

              <div class="flex items-center my-2">
                <v-otp-input
                  ref="newPinInput"
                  :input-classes="'otp-input ' + (pin_errors.mobile_app_pin ? 'error' : '')"
                  separator="-"
                  :num-inputs="6"
                  :should-auto-focus="false"
                  :is-input-num="true"
                  :input-type="showNewPin ? 'number' : 'password'"
                  @on-change="handleNewPinChange"
                  @on-complete="handleNewPinComplete"
                />
              </div>
              <span v-if="pin_errors.mobile_app_pin" class="text-danger text-sm block mt-1">
                {{ pin_errors.mobile_app_pin }}
              </span>
            </div>

            <!-- Confirm PIN (6 Boxes) -->
            <div class="mb-4">
              <div class="flex items-center mb-2">
                <small class="label font-semibold">Confirm 6-Digit PIN</small>
                <div
                  v-if="mobile_app_pin_confirmation && mobile_app_pin_confirmation.length > 0"
                  @click.stop="showConfirmPin = !showConfirmPin"
                  class="cursor-pointer text-xs flex items-center text-primary ml-3"
                >
                  <feather-icon :icon="showConfirmPin ? 'EyeOffIcon' : 'EyeIcon'" svgClasses="w-4 h-4 mr-1" />
                  <span>{{ showConfirmPin ? 'Mask PIN' : 'Reveal PIN' }}</span>
                </div>
              </div>

              <div class="flex items-center my-2">
                <v-otp-input
                  ref="confirmPinInput"
                  :input-classes="'otp-input ' + (pin_errors.mobile_app_pin_confirmation ? 'error' : '')"
                  separator="-"
                  :num-inputs="6"
                  :should-auto-focus="false"
                  :is-input-num="true"
                  :input-type="showConfirmPin ? 'number' : 'password'"
                  @on-change="handleConfirmPinChange"
                  @on-complete="handleConfirmPinComplete"
                />
              </div>
              <span v-if="pin_errors.mobile_app_pin_confirmation" class="text-danger text-sm block mt-1">
                {{ pin_errors.mobile_app_pin_confirmation }}
              </span>
            </div>

            <!-- Save & Reset Buttons -->
            <div class="flex flex-wrap items-center justify-start mt-6">
              <vs-button button="submit" class="mr-3">Update PIN</vs-button>
              <vs-button type="border" color="warning" @click="resetUpdateForm">Reset</vs-button>
            </div>
          </form>
        </div>
      </vx-card>
    </div>

  </div>
</template>

<script>
import OtpInput from '@bachdgvn/vue-otp-input'
import QrcodeVue from 'qrcode.vue'
import { Printd } from 'printd'
import moment from 'moment'
import profile from '@/apis/profile.js'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import ErrorMessages from '@/mixins/ErrorMessages'

const StaffRepository = RepositoryFactory.get('staff')

export default {
  name: 'MobileAppSecurity',
  components: {
    'v-otp-input': OtpInput,
    QrcodeVue
  },
  mixins: [ErrorMessages],
  computed: {
    userAvatar() {
      if (this.user_profile && this.user_profile.avatar) {
        return this.user_profile.avatar
      }
      const currentUser = (this.$store && this.$store.getters && this.$store.getters.currentUser) ||
        (this.$store && this.$store.state && this.$store.state.AppActiveUser) ||
        JSON.parse(localStorage.getItem('userInfo') || '{}')
      return (currentUser && (currentUser.avatar || currentUser.photoURL || currentUser.profile_photo_url)) || ''
    },
    userName() {
      if (this.user_profile && this.user_profile.name) {
        return this.user_profile.name
      }
      const currentUser = (this.$store && this.$store.getters && this.$store.getters.currentUser) ||
        (this.$store && this.$store.state && this.$store.state.AppActiveUser) ||
        JSON.parse(localStorage.getItem('userInfo') || '{}')
      return (currentUser && (currentUser.name || currentUser.displayName)) || 'User'
    },
    userRole() {
      if (this.user_profile) {
        return (this.user_profile.role && this.user_profile.role.name) || this.user_profile.job_title || ''
      }
      const currentUser = (this.$store && this.$store.getters && this.$store.getters.currentUser) ||
        (this.$store && this.$store.state && this.$store.state.AppActiveUser) ||
        JSON.parse(localStorage.getItem('userInfo') || '{}')
      return (currentUser && ((currentUser.role && currentUser.role.name) || currentUser.job_title)) || ''
    },
    userEmail() {
      if (this.user_profile && this.user_profile.email) {
        return this.user_profile.email
      }
      const currentUser = (this.$store && this.$store.getters && this.$store.getters.currentUser) ||
        (this.$store && this.$store.state && this.$store.state.AppActiveUser) ||
        JSON.parse(localStorage.getItem('userInfo') || '{}')
      return (currentUser && currentUser.email) || ''
    },
    userInitials() {
      if (!this.userName) return 'U'
      const parts = this.userName.trim().split(/\s+/)
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return parts[0].substring(0, 2).toUpperCase()
    },
    qrData() {
      return JSON.stringify({ email: this.userEmail })
    }
  },
  data() {
    return {
      companyLogo: require('@/assets/images/logo/its_logo_horizontal.png'),
      user_profile: null,

      // Update PIN state
      mobile_app_pin: '',
      mobile_app_pin_confirmation: '',
      showNewPin: false,
      showConfirmPin: false,
      pin_errors: {
        mobile_app_pin: '',
        mobile_app_pin_confirmation: ''
      },

      // View PIN state
      view_password: '',
      showViewPassword: false,
      is_pin_viewed: false,
      is_pin_masked: false,
      no_pin_message: '',
      pin_data: {
        mobile_app_pin: '',
        mobile_app_pin_updated_at: ''
      }
    }
  },
  async mounted() {
    try {
      const { data } = await profile.getCurrentUser()
      if (data && data.data) {
        this.user_profile = data.data
      }
    } catch (e) {}
  },
  methods: {
    formatDateTime(dateStr) {
      if (!dateStr) return ''
      return moment(dateStr).format('YYYY-MM-DD HH:mm:ss')
    },

    // OTP Handlers for New PIN
    handleNewPinChange(val) {
      this.mobile_app_pin = val || ''
      if (this.pin_errors.mobile_app_pin && this.mobile_app_pin.length === 6) {
        this.pin_errors.mobile_app_pin = ''
      }
      if (this.pin_errors.mobile_app_pin_confirmation && this.mobile_app_pin === this.mobile_app_pin_confirmation) {
        this.pin_errors.mobile_app_pin_confirmation = ''
      }
    },
    handleNewPinComplete(val) {
      this.mobile_app_pin = val
      this.pin_errors.mobile_app_pin = ''
      if (this.pin_errors.mobile_app_pin_confirmation && this.mobile_app_pin === this.mobile_app_pin_confirmation) {
        this.pin_errors.mobile_app_pin_confirmation = ''
      }
    },

    // OTP Handlers for Confirm PIN
    handleConfirmPinChange(val) {
      this.mobile_app_pin_confirmation = val || ''
      if (this.pin_errors.mobile_app_pin_confirmation && this.mobile_app_pin_confirmation === this.mobile_app_pin) {
        this.pin_errors.mobile_app_pin_confirmation = ''
      }
    },
    handleConfirmPinComplete(val) {
      this.mobile_app_pin_confirmation = val
      if (this.mobile_app_pin_confirmation === this.mobile_app_pin) {
        this.pin_errors.mobile_app_pin_confirmation = ''
      }
    },

    validatePinForm() {
      let isValid = true
      this.pin_errors = {
        mobile_app_pin: '',
        mobile_app_pin_confirmation: ''
      }

      if (!this.mobile_app_pin) {
        this.pin_errors.mobile_app_pin = 'Please enter a 6-digit PIN'
        isValid = false
      } else if (this.mobile_app_pin.length !== 6) {
        this.pin_errors.mobile_app_pin = 'PIN must be exactly 6 digits'
        isValid = false
      }

      if (!this.mobile_app_pin_confirmation) {
        this.pin_errors.mobile_app_pin_confirmation = 'Please confirm your 6-digit PIN'
        isValid = false
      } else if (this.mobile_app_pin_confirmation !== this.mobile_app_pin) {
        this.pin_errors.mobile_app_pin_confirmation = 'PIN confirmation does not match'
        isValid = false
      }

      return isValid
    },

    async onClickUpdatePin() {
      if (!this.validatePinForm()) {
        return
      }

      this.$vs.loading({
        container: '#update-pin-container',
        scale: 0.45
      })
      try {
        await StaffRepository.updateMobileAppPin({
          mobile_app_pin: this.mobile_app_pin,
          mobile_app_pin_confirmation: this.mobile_app_pin_confirmation
        })

        this.$vs.notify({
          title: 'Success',
          text: 'Mobile App PIN updated successfully',
          color: 'success',
          position: 'top-right',
          iconPack: 'feather',
          icon: 'icon-check'
        })

        // If PIN view is currently open, update displayed PIN
        if (this.is_pin_viewed) {
          this.pin_data.mobile_app_pin = this.mobile_app_pin
          this.pin_data.mobile_app_pin_updated_at = moment().format('YYYY-MM-DD HH:mm:ss')
        }
        this.no_pin_message = ''

        this.resetUpdateForm()
      } catch (error) {
        this.convertAndNotifyError(error)
      } finally {
        this.$vs.loading.close('#update-pin-container > .con-vs-loading')
      }
    },

    resetUpdateForm() {
      this.mobile_app_pin = ''
      this.mobile_app_pin_confirmation = ''
      this.pin_errors = {
        mobile_app_pin: '',
        mobile_app_pin_confirmation: ''
      }
      this.showNewPin = false
      this.showConfirmPin = false
      if (this.$refs.newPinInput && typeof this.$refs.newPinInput.clearInput === 'function') {
        this.$refs.newPinInput.clearInput()
      }
      if (this.$refs.confirmPinInput && typeof this.$refs.confirmPinInput.clearInput === 'function') {
        this.$refs.confirmPinInput.clearInput()
      }
    },

    // View PIN handlers
    async onClickViewPin() {
      this.no_pin_message = ''
      this.$vs.loading({
        container: '#view-pin-container',
        scale: 0.45
      })
      try {
        const response = await StaffRepository.viewMobileAppPin({
          password: this.view_password
        })
        const resData = (response && response.data && response.data.data) ? response.data.data : {}

        if (resData.message && !resData.mobile_app_pin) {
          this.no_pin_message = resData.message
          this.is_pin_viewed = false
          this.view_password = ''
          if (this.$refs.viewPinForm) {
            this.$refs.viewPinForm.reset()
          }
          return
        }

        this.pin_data = {
          mobile_app_pin: resData.mobile_app_pin || '',
          mobile_app_pin_updated_at: resData.mobile_app_pin_updated_at || ''
        }
        this.is_pin_viewed = true
        this.is_pin_masked = false
        this.view_password = ''
        if (this.$refs.viewPinForm) {
          this.$refs.viewPinForm.reset()
        }
      } catch (error) {
        const errData = error && error.response && error.response.data
        const notSetMessage = (errData && errData.data && errData.data.message) ||
          (errData && typeof errData.message === 'string' && (errData.message.toLowerCase().includes('not set') || errData.message.toLowerCase().includes('pin')) ? errData.message : null)

        if (notSetMessage) {
          this.no_pin_message = notSetMessage
          this.is_pin_viewed = false
          this.view_password = ''
          if (this.$refs.viewPinForm) {
            this.$refs.viewPinForm.reset()
          }
        } else {
          this.convertAndNotifyError(error)
        }
      } finally {
        this.$vs.loading.close('#view-pin-container > .con-vs-loading')
      }
    },

    onClickLockPin() {
      this.is_pin_viewed = false
      this.is_pin_masked = false
      this.no_pin_message = ''
      this.pin_data = {
        mobile_app_pin: '',
        mobile_app_pin_updated_at: ''
      }
      this.view_password = ''
    },

    copyPinToClipboard() {
      if (!this.pin_data.mobile_app_pin) return
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.pin_data.mobile_app_pin).then(() => {
          this.$vs.notify({
            title: 'Copied',
            text: 'PIN copied to clipboard',
            color: 'success',
            position: 'top-right',
            iconPack: 'feather',
            icon: 'icon-check'
          })
        }).catch(() => {})
      }
    },

    onClickPrintCard() {
      const cardEl = document.getElementById('mobile-app-business-card')
      if (!cardEl) return

      const p = new Printd()
      const styles = `
        @page {
          size: 85mm 55mm;
          margin: 0;
        }
        @media print {
          html, body {
            margin: 0;
            padding: 0;
            width: 85mm;
            height: 55mm;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        .business-card-preview {
          width: 85mm;
          height: 55mm;
          padding: 3mm 4.5mm 2.5mm 4.5mm;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          background: #ffffff;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          border: 1px solid #e2e8f0;
          position: relative;
          overflow: hidden;
        }
        .card-top-stripe {
          height: 1.2mm;
          width: 100%;
          background: linear-gradient(90deg, #E95594 0%, #F5D247 50%, #6EC4BB 100%) !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          position: absolute;
          top: 0;
          left: 0;
        }
        .card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 0.3mm solid #f1f5f9;
          padding-bottom: 1.5mm;
          padding-top: 0.8mm;
        }
        .card-logo {
          height: 5.5mm;
          max-width: 34mm;
          object-fit: contain;
        }
        .card-corp-badge {
          font-size: 4.8pt;
          font-weight: 800;
          letter-spacing: 0.5px;
          color: #E95594 !important;
          background: #fdf2f8 !important;
          border: 0.2mm solid #fbcfe8 !important;
          padding: 0.5mm 1.8mm;
          border-radius: 0.8mm;
          text-transform: uppercase;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .card-body {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex: 1;
          padding: 1.6mm 0;
        }
        .card-user-info {
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding-right: 2.5mm;
          flex: 1;
          min-width: 0;
        }
        .card-user-profile-wrap {
          display: flex;
          align-items: center;
          margin-bottom: 1mm;
        }
        .card-user-avatar {
          width: 9.5mm;
          height: 9.5mm;
          border-radius: 50%;
          object-fit: cover;
          margin-right: 2mm;
          border: 0.3mm solid #cbd5e1;
          flex-shrink: 0;
        }
        .card-user-avatar-placeholder {
          width: 9.5mm;
          height: 9.5mm;
          border-radius: 50%;
          background: #E95594 !important;
          color: #ffffff !important;
          font-weight: 800;
          font-size: 4pt;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 2mm;
          flex-shrink: 0;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .card-user-text {
          min-width: 0;
        }
        .card-user-name {
          font-size: 10pt;
          font-weight: 700;
          color: #1f1c24;
          line-height: 1.2;
          margin-bottom: 0.3mm;
        }
        .card-user-role {
          font-size: 6pt;
          font-weight: 800;
          color: #E95594 !important;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.3mm;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .card-user-email {
          font-size: 6pt;
          color: #475569;
          word-break: break-all;
          display: flex;
          align-items: center;
        }
        .card-user-email .email-icon {
          width: 2.2mm;
          height: 2.2mm;
          margin-right: 1mm;
          flex-shrink: 0;
        }
        .card-qr-column {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .card-qr-box {
          background: #ffffff;
          padding: 1mm;
          border: 0.3mm solid #cbd5e1;
          border-radius: 1.5mm;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .card-qr-box svg {
          width: 20mm !important;
          height: 20mm !important;
          display: block;
        }
        .card-qr-label {
          font-size: 4.8pt;
          font-weight: 700;
          color: #475569;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          margin-top: 1.2mm;
          text-align: center;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .card-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-top: 0.3mm solid #f1f5f9;
          padding-top: 1mm;
        }
        .card-footer-text {
          font-size: 4.5pt;
          font-weight: 700;
          color: #94a3b8;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }
        .brand-dots {
          display: inline-flex;
          align-items: center;
        }
        .brand-dots span {
          width: 1.6mm;
          height: 1.6mm;
          border-radius: 50%;
          display: inline-block;
          margin-left: 1mm;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .brand-dot-pink { background: #E95594 !important; }
        .brand-dot-yellow { background: #F5D247 !important; }
        .brand-dot-teal { background: #6EC4BB !important; }
      `
      p.print(cardEl, [styles], [], ({ launchPrint }) => {
        setTimeout(() => {
          launchPrint()
        }, 150)
      })
    }
  }
}
</script>

<style lang="scss">
.mobile-app-pin-card {
  .otp-input {
    width: 45px;
    height: 45px;
    padding: 2px;
    margin: 0 5px;
    font-size: 25px;
    border-radius: 10px;
    border: 1px solid rgba(0, 0, 0, 0.3);
    text-align: center;
    background-color: #fff;
    color: #2c2c2c;

    &.error {
      border: 1px solid red !important;
    }

    &:focus {
      border-color: rgba(var(--vs-primary), 1) !important;
      outline: none;
    }
  }

  .otp-input::-webkit-inner-spin-button,
  .otp-input::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}

.theme-dark .mobile-app-pin-card .otp-input {
  background-color: #262c49;
  color: #c2c6dc;
  border-color: #414561;
}

.show-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 0.75rem;
}

.bg-light-primary {
  background-color: rgba(var(--vs-primary), 0.05);
}

/* Business Card Preview Styles */
.business-card-preview {
  width: 360px;
  min-height: 226px;
  padding: 16px 18px 12px 18px;
  background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
  color: #1f1c24;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 12px 28px -6px rgba(0, 0, 0, 0.09), 0 4px 10px -2px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;

  .card-top-stripe {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #E95594 0%, #F5D247 50%, #6EC4BB 100%);
  }

  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #f1f5f9;
    padding-bottom: 10px;
    padding-top: 4px;
  }

  .card-logo {
    height: 26px;
    max-width: 135px;
    object-fit: contain;
  }

  .card-corp-badge {
    font-size: 8px;
    font-weight: 800;
    letter-spacing: 0.9px;
    color: #E95594;
    background: #fdf2f8;
    border: 1px solid #fbcfe8;
    padding: 3px 8px;
    border-radius: 4px;
    text-transform: uppercase;
  }

  .card-body {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 0;
  }

  .card-user-info {
    flex: 1;
    padding-right: 14px;
    min-width: 0;
  }

  .card-user-profile-wrap {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
  }

  .card-user-avatar {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    object-fit: cover;
    margin-right: 10px;
    border: 2px solid #ffffff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    flex-shrink: 0;
  }

  .card-user-avatar-placeholder {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: linear-gradient(135deg, #E95594 0%, #D83B7E 100%);
    color: #ffffff;
    font-weight: 800;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 10px;
    box-shadow: 0 2px 6px rgba(233, 85, 148, 0.25);
    flex-shrink: 0;
  }

  .card-user-text {
    min-width: 0;
  }

  .card-user-name {
    font-size: 15px;
    font-weight: 700;
    color: #1f1c24;
    line-height: 1.25;
    margin-bottom: 2px;
  }

  .card-user-role {
    font-size: 10px;
    font-weight: 800;
    color: #E95594;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    margin-bottom: 2px;
  }

  .card-user-email {
    font-size: 11px;
    color: #64748b;
    word-break: break-all;
    display: flex;
    align-items: center;

    .email-icon {
      margin-right: 5px;
      color: #94a3b8;
      flex-shrink: 0;
    }
  }

  .card-qr-column {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .card-qr-box {
    background: #ffffff;
    padding: 5px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.03);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .card-qr-label {
    font-size: 8px;
    font-weight: 700;
    color: #64748b;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    margin-top: 6px;
    text-align: center;
  }

  .card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-top: 1px solid #f1f5f9;
    padding-top: 8px;
    margin-top: 4px;
  }

  .card-footer-text {
    font-size: 8px;
    font-weight: 700;
    letter-spacing: 0.7px;
    color: #94a3b8;
    text-transform: uppercase;
  }

  .brand-dots {
    display: inline-flex;
    align-items: center;
    gap: 5px;

    span {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      display: inline-block;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    }
    .brand-dot-pink { background: #E95594; }
    .brand-dot-yellow { background: #F5D247; }
    .brand-dot-teal { background: #6EC4BB; }
  }
}
</style>
